import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";

export const ReassignApproverDialog = ({ po, onClose }) => {
  const qc = useQueryClient();
  const [approverId, setApproverId] = useState("");
  const [note, setNote] = useState("");
  const { data } = useQuery({ queryKey: ["approvers"], queryFn: async () => (await api.get("/approvers")).data, enabled: !!po });
  const close = () => { setApproverId(""); setNote(""); onClose(); };
  const options = (data?.items || []).filter((u) => u.user_id !== po?.approval_approver_user_id);

  const reassign = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${po.id}/reassign-approver`, { approver_user_id: approverId, note })).data,
    onSuccess: (r) => {
      ["my-approvals", "approval-stats", "purchase-orders"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      const who = r.approver.name || r.approver.email;
      r.email_sent ? toast.success(`Reassigned to ${who} — approval email sent`) : toast.warning(`Reassigned to ${who}, but the email could not be sent`);
      close();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <Dialog open={!!po} onOpenChange={(o) => { if (!o) { close(); } }}>
      <DialogContent className="rounded-sm" data-testid="reassign-approver-dialog">
        <DialogHeader><DialogTitle>Reassign approver · {po?.doc_no}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Currently assigned to <b data-testid="reassign-current-approver">{po?.approval_approver_name || "Unassigned"}</b>. The new approver gets a fresh approval email and the old links stop working.</p>
        <Select value={approverId} onValueChange={setApproverId}>
          <SelectTrigger className="rounded-sm" data-testid="reassign-approver-select"><SelectValue placeholder="Choose new approver" /></SelectTrigger>
          <SelectContent>
            {options.map((u) => (
              <SelectItem key={u.user_id} value={u.user_id} data-testid={`reassign-option-${u.user_id}`}>{u.name || u.email} · {u.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional)" className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" data-testid="reassign-note" />
        <DialogFooter>
          <Button variant="outline" onClick={close} className="rounded-sm" data-testid="reassign-cancel">Cancel</Button>
          <Button onClick={() => reassign.mutate()} disabled={!approverId || reassign.isPending} className="rounded-sm" data-testid="reassign-confirm">{reassign.isPending ? "Reassigning…" : "Reassign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
